import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useCart } from '../CartContext';
import { useSession } from '../SessionContext';
import { placeOrder, createPaymentSession, completePayment } from '../api';
import { v4 as uuidv4 } from 'uuid';
import MembershipModal from './MembershipModal';
import './Checkout.css';

function formatPrice(amount) {
  return `£${Number(amount).toFixed(2)}`;
}

const Checkout = ({ venue, venueId, tableNumber, checkoutId: checkoutIdProp, onBack, onOrderPlaced }) => {
  const { items, total, clearCart } = useCart();
  const { sessionToken, setSessionToken, member, setMember, checkoutId: checkoutIdCtx } = useSession();
  const checkoutId = checkoutIdProp ?? checkoutIdCtx ?? null;

  const paymentMethods = venue?.payment_methods || [];
  const hasPayAtCounter = paymentMethods.includes('pay_at_counter') || paymentMethods.includes('cash');
  const hasWorldpay = paymentMethods.includes('worldpay');

  const [selectedPayment, setSelectedPayment] = useState(
    hasPayAtCounter ? 'pay_at_counter' : hasWorldpay ? 'worldpay' : null
  );
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [paymentSessionUrl, setPaymentSessionUrl] = useState(null);
  const [paymentReference, setPaymentReference] = useState(null);
  const [orderNumber, setOrderNumber] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [sessionRetryCount, setSessionRetryCount] = useState(0);
  const worldpayRef = useRef(null);
  const iframeContainerRef = useRef(null);

  // Calculate per-item discounts using membership discount rules
  const discountRules = member?.discount_rules || [];

  const { discountAmount, discountDetails } = (() => {
    if (discountRules.length === 0) return { discountAmount: 0, discountDetails: [] };

    let totalDiscount = 0;
    const details = [];

    for (const item of items) {
      // Find a matching rule for this item's product/variation
      const matchingRule = discountRules.find(rule => {
        if (!rule.products || rule.products.length === 0) return true; // applies to all products
        return rule.products.some(p => {
          const pidMatch = String(p.productId) === String(item.product_id);
          if (!p.variationId) return pidMatch; // rule applies to whole product
          return pidMatch && String(p.variationId) === String(item.variation_id);
        });
      });

      if (!matchingRule) continue;

      // Check remaining uses (null = unlimited)
      if (matchingRule.remaining !== null && matchingRule.remaining <= 0) continue;

      let itemDiscount = 0;
      if (matchingRule.type === 'percentage') {
        itemDiscount = item.price * item.quantity * (matchingRule.amount / 100);
      } else if (matchingRule.type === 'value') {
        itemDiscount = Math.min(matchingRule.amount * item.quantity, item.price * item.quantity);
      } else if (matchingRule.type === 'flat_price') {
        const diff = item.price - matchingRule.amount;
        itemDiscount = diff > 0 ? diff * item.quantity : 0;
      }

      if (itemDiscount > 0) {
        totalDiscount += itemDiscount;
        details.push({
          itemId: item.id,
          ruleId: matchingRule.id,
          type: matchingRule.type,
          amount: matchingRule.amount,
          saved: itemDiscount
        });
      }
    }

    return { discountAmount: totalDiscount, discountDetails: details };
  })();

  const hasDiscount = discountAmount > 0;
  const grandTotal = total - discountAmount;

  const handleMemberVerified = (memberData) => {
    setMember(memberData);
    if (memberData.session_token) {
      setSessionToken(memberData.session_token);
    }
    setShowMemberModal(false);
  };

  const handlePlaceOrder = async () => {
    if (submitting || submitted) return;
    setSubmitting(true);
    setErrorMsg(null);

    const orderPayload = {
      venue_id: venueId,
      table_number: tableNumber,
      session_token: sessionToken,
      idempotency_key: uuidv4(),
      payment_method: selectedPayment,
      checkout_id: checkoutId || null,
      membership_id: member?.membership_id || null,
      discount_amount: discountAmount > 0 ? parseFloat(discountAmount.toFixed(2)) : 0,
      membership_redemptions: discountDetails.map(d => ({
        rule_id: d.ruleId,
        quantity: 1,
      })),
      items: items.map(item => ({
        product_id: item.product_id,
        variation_id: item.variation_id,
        quantity: item.quantity,
        modifiers: item.modifiers,
        notes: item.notes,
        unit_price: item.price,
      })),
    };

    try {
      const response = await placeOrder(orderPayload);
      if (response.session_token) {
        setSessionToken(response.session_token);
      }
      setSubmitted(true);
      clearCart();
      onOrderPlaced(response);
    } catch (err) {
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  const handleSelectWorldpay = useCallback(async (isRetry = false) => {
    setSelectedPayment('worldpay');
    setPaymentStatus('loading');
    if (!isRetry) setPaymentError(null);

    try {
      const sessionData = {
        venue_id: venueId,
        checkout_id: checkoutId || null,
        table_number: tableNumber,
        session_token: sessionToken,
        membership_id: member?.membership_id || null,
        discount_amount: discountAmount > 0 ? parseFloat(discountAmount.toFixed(2)) : 0,
        membership_redemptions: discountDetails.map(d => ({
          rule_id: d.ruleId,
          quantity: 1,
        })),
        items: items.map(item => ({
          product_id: item.product_id,
          variation_id: item.variation_id,
          quantity: item.quantity,
          modifiers: item.modifiers,
          notes: item.notes,
        })),
      };

      const result = await createPaymentSession(sessionData);
      setPaymentSessionUrl(result.session_url);
      setPaymentReference(result.payment_reference);
      setOrderNumber(result.order_number || null);
      // Save payment context for redirect fallback (if WPCL library doesn't intercept the redirect)
      localStorage.setItem('worldpay_pending_payment', JSON.stringify({
        payment_reference: result.payment_reference,
        venue_id: venueId,
        return_url: window.location.href
      }));
      setPaymentStatus('ready');
    } catch (err) {
      setPaymentError(err.message || 'Failed to set up card payment. Please try again.');
      setPaymentStatus('failed');
    }
  }, [venueId, checkoutId, tableNumber, sessionToken, member, discountAmount, discountDetails, items]);

  const retryPaymentSession = useCallback(async () => {
    if (worldpayRef.current) {
      try { worldpayRef.current.destroy(); } catch (e) { /* ignore */ }
      worldpayRef.current = null;
    }
    setPaymentSessionUrl(null);
    setPaymentReference(null);
    await handleSelectWorldpay(true);
  }, [handleSelectWorldpay]);

  const handleSwitchToCounter = useCallback(() => {
    if (worldpayRef.current) {
      try { worldpayRef.current.destroy(); } catch (e) { /* ignore */ }
      worldpayRef.current = null;
    }
    setSelectedPayment('pay_at_counter');
    setPaymentSessionUrl(null);
    setPaymentReference(null);
    setPaymentStatus(null);
    setPaymentError(null);
    setSessionRetryCount(0);
  }, []);

  useEffect(() => {
    if (paymentStatus !== 'ready' || !paymentSessionUrl || !iframeContainerRef.current) return;

    if (worldpayRef.current) {
      try { worldpayRef.current.destroy(); } catch (e) { /* ignore */ }
    }

    const handleResult = async (responseData) => {
      const status = responseData?.order?.status;
      const errorReason = responseData?.order?.errorReason || responseData?.order?.description || '';

      console.log('[WORLDPAY-CLIENT] Payment status:', status, 'errorReason:', errorReason);

      switch (status) {
        case 'success':
          setPaymentStatus('processing');
          localStorage.removeItem('worldpay_pending_payment');
          try {
            const orderResult = await completePayment({
              payment_reference: paymentReference,
              worldpay_order_key: responseData.order.orderKey,
            });
            if (orderResult.session_token) {
              setSessionToken(orderResult.session_token);
            }
            clearCart();
            onOrderPlaced(orderResult);
          } catch (err) {
            setPaymentError(
              orderNumber
                ? `Payment received but order creation failed. Your payment is safe — please quote order number ${orderNumber} to staff.`
                : 'Payment received but order creation failed. Your payment is safe — please contact staff.'
            );
            setPaymentStatus('failed');
          }
          break;

        case 'failure':
        case 'error':
          setPaymentError(`Payment was declined${errorReason ? `: ${errorReason}` : ''}. Please try again or use a different card.`);
          retryPaymentSession();
          break;

        case 'cancelled_by_shopper':
          setPaymentError('Payment cancelled. You can try again or pay at the counter.');
          setPaymentStatus('failed');
          break;

        case 'session_expired':
          if (sessionRetryCount < 2) {
            setSessionRetryCount(prev => prev + 1);
            retryPaymentSession();
          } else {
            setPaymentError('Payment session expired. Please try again.');
            setPaymentStatus('failed');
          }
          break;

        default:
          setPaymentError(`Something went wrong (status: ${status || 'unknown'}). Please try again.`);
          setPaymentStatus('failed');
      }
    };

    const initWorldpay = () => {
      console.log('[WORLDPAY-CLIENT] Setting up iframe with URL:', paymentSessionUrl);
      const appOrigin = window.location.origin;
      const libraryObject = new window.WPCL.Library();
      libraryObject.setup({
        url: paymentSessionUrl,
        type: 'iframe',
        inject: 'immediate',
        target: 'worldpay-iframe-container',
        accessibility: true,
        debug: true,
        // Result URLs must match those sent in the API request — the WPCL library
        // uses them to intercept redirects and fire resultCallback instead of navigating
        successURL: `${appOrigin}/success`,
        failureURL: `${appOrigin}/failure`,
        cancelURL: `${appOrigin}/cancel`,
        pendingURL: `${appOrigin}/pending`,
        errorURL: `${appOrigin}/error`,
        expiryURL: `${appOrigin}/expiry`,
        resultCallback: function(responseData) {
          console.log('[WORLDPAY-CLIENT] resultCallback fired:', JSON.stringify(responseData));
          handleResult(responseData);
        },
        flowCallback: function(result) {
          console.log('[WORLDPAY-CLIENT] flowCallback fired:', JSON.stringify(result));
        },
      });
      worldpayRef.current = libraryObject;
      console.log('[WORLDPAY-CLIENT] Library setup complete');

      // Apple Pay requires the "payment" permission policy on the iframe.
      // The WPCL library doesn't add this, so we set it after injection.
      const container = document.getElementById('worldpay-iframe-container');
      if (container) {
        const iframe = container.querySelector('iframe');
        if (iframe) {
          iframe.setAttribute('allow', 'payment');
        } else {
          // iframe may be injected async — observe for it
          const observer = new MutationObserver(() => {
            const el = container.querySelector('iframe');
            if (el) {
              el.setAttribute('allow', 'payment');
              observer.disconnect();
            }
          });
          observer.observe(container, { childList: true, subtree: true });
        }
      }
    };

    // If the WPCL script didn't load (e.g. mobile Safari), load it dynamically
    if (window.WPCL) {
      try {
        initWorldpay();
      } catch (err) {
        console.error('[WORLDPAY-CLIENT] Setup error:', err);
        setPaymentError('Failed to load payment form. Please try again.');
        setPaymentStatus('failed');
      }
    } else {
      const script = document.createElement('script');
      script.src = 'https://payments.worldpay.com/resources/hpp/integrations/embedded/js/hpp-embedded-integration-library.js';
      script.onload = () => {
        try {
          initWorldpay();
        } catch (err) {
          console.error('[WORLDPAY-CLIENT] Setup error after dynamic load:', err);
          setPaymentError('Failed to load payment form. Please try again.');
          setPaymentStatus('failed');
        }
      };
      script.onerror = () => {
        setPaymentError('Unable to load payment provider. Please check your connection and try again.');
        setPaymentStatus('failed');
      };
      document.head.appendChild(script);
    }

    return () => {
      if (worldpayRef.current) {
        try { worldpayRef.current.destroy(); } catch (e) { /* ignore */ }
        worldpayRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStatus, paymentSessionUrl]);

  return (
    <div className="checkout-screen">
      {/* Header */}
      <div className="checkout-header">
        <button className="checkout-back-btn" onClick={onBack} aria-label="Back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h2 className="checkout-title">Checkout</h2>
      </div>

      {/* Scrollable body */}
      <div className="checkout-content">

        {/* Order summary */}
        <section className="checkout-section">
          <h3 className="checkout-section-title">Order Summary</h3>
          <ul className="checkout-items-list">
            {items.map(item => {
              const modifierLabels = item.modifiers && Object.keys(item.modifiers).length > 0
                ? Object.values(item.modifiers).flat().join(', ')
                : null;
              return (
                <li key={item.id} className="checkout-item">
                  <div className="checkout-item-left">
                    <span className="checkout-item-qty">{item.quantity}×</span>
                    <div className="checkout-item-info">
                      <span className="checkout-item-name">
                        {item.product_name}
                        {item.variation_name && (
                          <span className="checkout-item-variation"> — {item.variation_name}</span>
                        )}
                      </span>
                      {modifierLabels && (
                        <span className="checkout-item-modifiers">+ {modifierLabels}</span>
                      )}
                    </div>
                  </div>
                  <span className="checkout-item-total">{formatPrice(item.price * item.quantity)}</span>
                </li>
              );
            })}
          </ul>
        </section>

        {/* Membership */}
        <section className="checkout-section">
          {member ? (
            <div className="checkout-member-verified">
              <div className="checkout-member-verified-info">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <span className="checkout-member-name">{member.member_name}</span>
              </div>
              <span className="checkout-member-badge">Member</span>
            </div>
          ) : (
            <button className="checkout-member-btn" onClick={() => setShowMemberModal(true)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              I'm a member
            </button>
          )}
        </section>

        {/* Payment method */}
        <section className="checkout-section">
          <h3 className="checkout-section-title">Payment Method</h3>
          <div className="checkout-payment-options">
            {hasPayAtCounter && (
              <button
                className={`checkout-payment-card${selectedPayment === 'pay_at_counter' ? ' checkout-payment-card--selected' : ''}`}
                onClick={() => selectedPayment === 'worldpay' ? handleSwitchToCounter() : setSelectedPayment('pay_at_counter')}
                type="button"
              >
                <span className="checkout-payment-radio" aria-hidden="true" />
                <div className="checkout-payment-card-body">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2" />
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                  </svg>
                  <span className="checkout-payment-label">Pay at Counter</span>
                </div>
              </button>
            )}
            {hasWorldpay && (
              <button
                className={`checkout-payment-card${selectedPayment === 'worldpay' ? ' checkout-payment-card--selected' : ''}`}
                onClick={handleSelectWorldpay}
                type="button"
                disabled={paymentStatus === 'loading' || paymentStatus === 'processing'}
              >
                <span className="checkout-payment-radio" aria-hidden="true" />
                <div className="checkout-payment-card-body">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                    <line x1="1" y1="10" x2="23" y2="10" />
                  </svg>
                  <span className="checkout-payment-label">Pay by Card</span>
                </div>
              </button>
            )}
          </div>
        </section>

        {paymentError && (
          <div className="checkout-payment-error" role="alert">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            <span>{paymentError}</span>
          </div>
        )}

        {selectedPayment === 'worldpay' && (
          <section className="checkout-section checkout-worldpay-section">
            {paymentStatus === 'loading' && (
              <div className="checkout-worldpay-loading">
                <span className="checkout-spinner" aria-hidden="true" />
                <span>Setting up secure payment...</span>
              </div>
            )}
            {paymentStatus === 'processing' && (
              <div className="checkout-worldpay-loading">
                <span className="checkout-spinner" aria-hidden="true" />
                <span>Confirming payment...</span>
              </div>
            )}
            <div
              id="worldpay-iframe-container"
              ref={iframeContainerRef}
              className="checkout-worldpay-iframe"
              style={{ display: paymentStatus === 'ready' ? 'block' : 'none' }}
            />
          </section>
        )}

        {/* Order total */}
        <section className="checkout-section checkout-section--total">
          <div className="checkout-total-row">
            <span className="checkout-total-label">Subtotal</span>
            <span className="checkout-total-value">{formatPrice(total)}</span>
          </div>
          {hasDiscount && (
            <div className="checkout-total-row checkout-total-row--discount">
              <span className="checkout-total-label">
                Member discount
              </span>
              <span className="checkout-total-value">−{formatPrice(discountAmount)}</span>
            </div>
          )}
          <div className="checkout-total-row checkout-total-row--grand">
            <span className="checkout-total-label">Total</span>
            <span className="checkout-total-value">{formatPrice(grandTotal)}</span>
          </div>
        </section>

        {errorMsg && (
          <div className="checkout-error" role="alert">{errorMsg}</div>
        )}
      </div>

      {/* Sticky footer — Place Order */}
      {selectedPayment === 'pay_at_counter' && hasPayAtCounter && (
        <div className="checkout-footer">
          <button
            className="checkout-place-order-btn"
            onClick={handlePlaceOrder}
            disabled={submitting || submitted || items.length === 0}
          >
            {submitting ? (
              <span className="checkout-spinner" aria-hidden="true" />
            ) : (
              `Place Order · ${formatPrice(grandTotal)}`
            )}
          </button>
        </div>
      )}

      {showMemberModal && (
        <MembershipModal
          venueId={venueId}
          onClose={() => setShowMemberModal(false)}
          onVerified={handleMemberVerified}
        />
      )}
    </div>
  );
};

export default Checkout;
